import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { StatWidget } from '@/components/widgets/StatWidget';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { Select2 } from '@/components/forms/Select2';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import type { Select2Option } from '@/components/forms/Select2';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import { http } from '@/api/client/httpClient';
import { filterDataApi } from '@/api/services/filterData';
import { formatNumber, formatDateTime } from '@/utils/format';
import { moneyTransactionsApi } from '@/api/services/moneyTransactions';
import type { MoneyTransactionRow, MoneyTransactionsInput } from '@/api/types/moneyTransactions';
import { EditProviderDialog } from './EditProviderDialog';

const EDIT_PERMISSION = 'User.SafeguardingReconciliation.MoneyTransactions.EditProvider';

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Mirrors the legacy syncAmountVisibility/cleanFilters: drop empty strings, nulls and
// empty arrays so the enum/int List<T> params never bind an invalid "" value.
function cleanFilters(f: MoneyTransactionsInput): MoneyTransactionsInput {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) {
      const items = v.filter((x) => x !== '' && x != null);
      if (items.length === 0) continue;
      out[k] = items;
    } else {
      out[k] = v;
    }
  }
  return out as MoneyTransactionsInput;
}

interface FilterState {
  startDate: string;
  endDate: string;
  uniqueId: string;
  login: string;
  ticketId: string;
  amount: string;
  amountOperation: string;
  risk: string[];
  connector: string[];
  country: string[];
  currency: string[];
  provider: string[];
  providerType: string[];
  mifidClassification: string[];
  group: string[];
  transactionType: string[];
  matchStatus: string[];
  matchType: string[];
}

function emptyFilters(): FilterState {
  const t = todayIso();
  return {
    startDate: t,
    endDate: t,
    uniqueId: '',
    login: '',
    ticketId: '',
    amount: '',
    amountOperation: '',
    risk: [],
    connector: [],
    country: [],
    currency: [],
    provider: [],
    providerType: [],
    mifidClassification: [],
    group: [],
    transactionType: [],
    matchStatus: [],
    matchType: [],
  };
}

// Build the backend input DTO from UI state (applied filters).
function toInput(f: FilterState): MoneyTransactionsInput {
  return cleanFilters({
    startDate: f.startDate || undefined,
    endDate: f.endDate || undefined,
    uniqueId: f.uniqueId || undefined,
    login: f.login || undefined,
    ticketId: f.ticketId ? Number(f.ticketId) : undefined,
    amount: f.amountOperation && f.amount ? Number(f.amount) : undefined,
    amountOperation: f.amountOperation ? Number(f.amountOperation) : undefined,
    risk: f.risk.map(Number),
    connector: f.connector.map(Number),
    country: f.country,
    currency: f.currency,
    provider: f.provider,
    providerType: f.providerType,
    mifidClassification: f.mifidClassification.map(Number),
    group: f.group,
    transactionType: f.transactionType.map(Number),
    matchStatus: f.matchStatus.map(Number),
    matchType: f.matchType.map(Number),
  });
}

function renderProviderFlag(row: MoneyTransactionRow) {
  const text = row.provider && row.provider.length ? row.provider : '-';
  return (
    <>
      {text}
      {row.providerChanged && (
        <i className="fa fa-flag text-danger ms-1" title="Affected by a provider change" aria-hidden="true" />
      )}
    </>
  );
}

export function MoneyTransactionsPage() {
  const toast = useToast();
  const can = usePermission();
  const canEdit = can(EDIT_PERMISSION);
  const today = todayIso();

  const [draft, setDraft] = useState<FilterState>(emptyFilters);
  const [applied, setApplied] = useState<FilterState>(emptyFilters);

  const appliedInput = useMemo(() => toInput(applied), [applied]);

  // --- Filter option lists (loaded once) ---
  const optionQueries = useQueries({
    queries: [
      { queryKey: ['mt-opts', 'risk'], queryFn: moneyTransactionsApi.getRiskOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'connector'], queryFn: filterDataApi.getNonTerminalConnectors, staleTime: Infinity },
      { queryKey: ['mt-opts', 'country'], queryFn: filterDataApi.getCountries, staleTime: Infinity },
      { queryKey: ['mt-opts', 'currency'], queryFn: moneyTransactionsApi.getCurrencyOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'provider'], queryFn: moneyTransactionsApi.getProviderOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'providerType'], queryFn: moneyTransactionsApi.getProviderTypeOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'mifid'], queryFn: filterDataApi.getMifidClassifications, staleTime: Infinity },
      { queryKey: ['mt-opts', 'group'], queryFn: filterDataApi.getNonTerminalGroups, staleTime: Infinity },
      { queryKey: ['mt-opts', 'transactionType'], queryFn: moneyTransactionsApi.getTransactionTypeOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'amountOperator'], queryFn: moneyTransactionsApi.getAmountOperatorOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'matchStatus'], queryFn: moneyTransactionsApi.getMatchStatusOptions, staleTime: Infinity },
      { queryKey: ['mt-opts', 'matchType'], queryFn: moneyTransactionsApi.getMatchTypeOptions, staleTime: Infinity },
    ],
  });
  const [
    riskOpts,
    connectorOpts,
    countryOptsRaw,
    currencyOpts,
    providerOpts,
    providerTypeOpts,
    mifidOpts,
    groupOpts,
    transactionTypeOpts,
    amountOperatorOpts,
    matchStatusOpts,
    matchTypeOpts,
  ] = optionQueries.map((q) => q.data ?? []);

  // The MoneyTransactions filter uses the country DISPLAY TEXT as the value (the
  // contributor sets Value = Text), so remap value -> label (shared getCountries
  // returns the numeric country id as value).
  const countryOpts: Select2Option[] = useMemo(
    () => (countryOptsRaw as Select2Option[]).map((o) => ({ value: o.label, label: o.label })),
    [countryOptsRaw],
  );

  // amountOperation gets a static "All" sentinel prepended (legacy).
  const amountOperatorOptions: Select2Option[] = useMemo(
    () => [{ value: '', label: 'All' }, ...(amountOperatorOpts as Select2Option[])],
    [amountOperatorOpts],
  );

  // --- Analytics (today→today on mount; reloads on Fetch/Clear) ---
  const analytics = useQuery({
    queryKey: ['mt-analytics', appliedInput],
    queryFn: () => moneyTransactionsApi.getAnalytics(appliedInput),
  });

  // --- Server grid ---
  const table = useServerDataTable<MoneyTransactionRow, MoneyTransactionsInput>({
    queryKey: ['mt-rows'],
    fetcher: (req) => moneyTransactionsApi.getForDataTable(req),
    pageSize: 5,
    extraParams: appliedInput,
  });

  // --- Edit provider (maker-checker) flow ---
  const [editState, setEditState] = useState<
    { row: MoneyTransactionRow; newProvider: string } | null
  >(null);
  // Inline row edit (legacy $.finkitRowEdit): the Provider cell of this row turns into a
  // dropdown and the Actions cell shows save/cancel icons. Save runs beginEdit (pending
  // check → comment modal).
  const [editing, setEditing] = useState<{ id: MoneyTransactionRow['id']; provider: string } | null>(null);

  function saveInline(row: MoneyTransactionRow) {
    const picked = editing?.provider ?? null;
    setEditing(null);
    void beginEdit(row, picked);
  }

  async function beginEdit(row: MoneyTransactionRow, newProviderValue: string | null) {
    if (!newProviderValue) return;
    if (newProviderValue === (row.provider ?? '')) {
      toast.warning('No change: the transaction is already assigned to this provider.');
      return;
    }
    try {
      const pending = await moneyTransactionsApi.hasPendingProviderChange(row.id);
      if (pending) {
        toast.warning('A pending provider-change request already exists for this transaction.');
        return;
      }
      setEditState({ row, newProvider: newProviderValue });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to check pending requests.');
    }
  }

  async function submitEdit(comment: string) {
    if (!editState) return;
    try {
      await moneyTransactionsApi.updateProvider({
        id: editState.row.id,
        provider: editState.newProvider,
        comment,
      });
      toast.success('Your provider change request was submitted for approval.');
      setEditState(null);
      table.refetch();
      analytics.refetch();
    } catch (err) {
      // Close the modal so the error toast stays visible (legacy closeAfterSave).
      setEditState(null);
      toast.error(err instanceof ApiError ? err.message : 'Failed to submit the provider change request.');
    }
  }

  // --- Actions ---
  function fetchData() {
    setApplied(draft);
    table.setPageIndex(0);
  }

  function clearFilters() {
    // Legacy clearFilters: reset text/select fields; date range untouched.
    setDraft((f) => ({
      ...emptyFilters(),
      startDate: f.startDate,
      endDate: f.endDate,
    }));
    setApplied((f) => ({
      ...emptyFilters(),
      startDate: f.startDate,
      endDate: f.endDate,
    }));
    table.setPageIndex(0);
  }

  async function extract() {
    try {
      const res = await http.get(moneyTransactionsApi.extractExcelUrl, {
        params: appliedInput,
        responseType: 'blob',
        paramsSerializer: { indexes: null },
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Money Transactions.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export Money Transactions.');
    }
  }

  // --- Filter fields (order mirrors the contributor) ---
  const fields = [
    {
      node: (
        <div key="date">
          <label className="form-label">Date Range</label>
          <DateRangePicker
            mode="range"
            maxDate={today}
            value={{ start: draft.startDate, end: draft.endDate }}
            onChange={(v) => setDraft((f) => ({ ...f, startDate: v.start, endDate: v.end }))}
          />
        </div>
      ),
      pinned: true,
      width: 270,
    },
    <div key="uniqueId">
      <label className="form-label">Unique Id</label>
      <input
        className="form-control"
        value={draft.uniqueId}
        onChange={(e) => setDraft((f) => ({ ...f, uniqueId: e.target.value }))}
      />
    </div>,
    <div key="login">
      <label className="form-label">Login</label>
      <input
        className="form-control"
        value={draft.login}
        onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value }))}
      />
    </div>,
    <Select2Multi key="risk" label="Risk" value={draft.risk} options={riskOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, risk: v }))} />,
    <Select2Multi key="connector" label="Connector" value={draft.connector} options={connectorOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, connector: v }))} />,
    <Select2Multi key="country" label="Country" value={draft.country} options={countryOpts} onChange={(v) => setDraft((f) => ({ ...f, country: v }))} />,
    <Select2Multi key="currency" label="Currency" value={draft.currency} options={currencyOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, currency: v }))} />,
    <Select2Multi key="provider" label="Provider" value={draft.provider} options={providerOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, provider: v }))} />,
    <Select2Multi key="providerType" label="Provider Type" value={draft.providerType} options={providerTypeOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, providerType: v }))} />,
    <Select2Multi key="mifid" label="Mifid Classification" value={draft.mifidClassification} options={mifidOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, mifidClassification: v }))} />,
    <Select2Multi key="group" label="Group" value={draft.group} options={groupOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, group: v }))} />,
    <Select2Multi key="transactionType" label="Transaction Type" value={draft.transactionType} options={transactionTypeOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, transactionType: v }))} />,
    <Select2
      key="amountOperation"
      label="Amount Filter"
      placeholder="All"
      value={draft.amountOperation}
      options={amountOperatorOptions}
      onChange={(v) =>
        // Legacy syncAmountVisibility: clearing the operator resets the amount.
        setDraft((f) => ({ ...f, amountOperation: v ?? '', amount: v ? f.amount : '' }))
      }
    />,
    // Amount RC — hidden until an operator is chosen (legacy .js-amount-filter).
    ...(draft.amountOperation
      ? [
          <div key="amount">
            <label className="form-label">Amount RC</label>
            <input
              type="number"
              min={0}
              step="any"
              className="form-control"
              value={draft.amount}
              onChange={(e) => setDraft((f) => ({ ...f, amount: e.target.value }))}
            />
          </div>,
        ]
      : []),
    <div key="ticketId">
      <label className="form-label">Ticket ID</label>
      <input
        className="form-control"
        value={draft.ticketId}
        onChange={(e) => setDraft((f) => ({ ...f, ticketId: e.target.value }))}
      />
    </div>,
    <Select2Multi key="matchStatus" label="Matched" value={draft.matchStatus} options={matchStatusOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, matchStatus: v }))} />,
    <Select2Multi key="matchType" label="Match Type" value={draft.matchType} options={matchTypeOpts as Select2Option[]} onChange={(v) => setDraft((f) => ({ ...f, matchType: v }))} />,
  ];

  const actions = (
    <>
      <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetchData} loading={table.isFetching || analytics.isFetching}>
        Fetch
      </AsyncButton>
      <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={extract}>
        Extract
      </AsyncButton>
      <button type="button" className="btn btn-link btn-sm" onClick={clearFilters}>
        Clear All Filters
      </button>
    </>
  );

  const a = analytics.data;

  // --- Grid columns (20 visible, DTO order). Searchable=false → filterable:false. ---
  const columns: DataGridColumn<MoneyTransactionRow>[] = [
    { key: 'ticket', title: 'Ticket', className: 'text-start', value: (r) => r.ticket, filterable: false },
    { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login, filterable: false },
    { key: 'uniqueId', title: 'Unique Id', className: 'text-start', value: (r) => r.uniqueId, filterable: false },
    { key: 'name', title: 'Name', className: 'text-start', value: (r) => r.name },
    { key: 'amountRc', title: 'Amount RC', className: 'text-end', value: (r) => r.amountRc, cell: (r) => formatNumber(r.amountRc, 2) },
    { key: 'currency', title: 'Acc Currency', className: 'text-start', value: (r) => r.currency },
    { key: 'amount', title: 'Amount AC', className: 'text-end', value: (r) => r.amount, cell: (r) => formatNumber(r.amount, 2) },
    { key: 'transactionFee', title: 'Transaction Fee', className: 'text-end', value: (r) => r.transactionFee, cell: (r) => formatNumber(r.transactionFee, 2) },
    { key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country, filterable: false },
    {
      key: 'provider',
      title: 'Provider',
      className: 'text-start',
      value: (r) => r.provider,
      filterable: false,
      cell: (r) =>
        canEdit && editing?.id === r.id ? (
          <div style={{ minWidth: 180 }}>
            <Select2
              value={editing.provider}
              options={providerOpts as Select2Option[]}
              allowClear={false}
              placeholder="Select provider"
              onChange={(v) => setEditing({ id: r.id, provider: v ?? '' })}
            />
          </div>
        ) : (
          renderProviderFlag(r)
        ),
    },
    { key: 'providerType', title: 'Provider Type', className: 'text-start', value: (r) => r.providerType },
    { key: 'dateOfTransaction', title: 'Date Of Transaction', className: 'text-start', value: (r) => r.dateOfTransaction, cell: (r) => formatDateTime(r.dateOfTransaction) },
    { key: 'mifidClassification', title: 'Mifid Classification', className: 'text-start', value: (r) => r.mifidClassification },
    { key: 'risk', title: 'Risk', className: 'text-start', value: (r) => r.risk },
    { key: 'incomeSource', title: 'Income Source', className: 'text-start', value: (r) => r.incomeSource },
    { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
    { key: 'matched', title: 'Matched', className: 'text-start', value: (r) => r.matched },
    { key: 'matchedStatement', title: 'Payment Id', className: 'text-start', value: (r) => r.matchedStatement, cell: (r) => r.matchedStatement || 'N/A' },
    { key: 'matchType', title: 'Match Type', className: 'text-start', value: (r) => r.matchType },
    { key: 'matchDate', title: 'Match Date', className: 'text-start', value: (r) => r.matchDate, cell: (r) => (r.matchDate ? formatDateTime(r.matchDate) : '') },
  ];

  return (
    <div className="money-transactions-page">
      <PageHeader title="Money Transactions" breadcrumb={['Home', 'Money Transactions']} />

      <div className="card custom-card">
        <div className="card-header border-bottom">
          <FilterBar card={false} fields={fields} visibleCount={4} actions={actions} />
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-4 col-lg">
              <StatWidget label="Total Deposits (RC)" value={formatNumber(a?.totalDepositsRc ?? 0, 2)} icon="fe fe-arrow-down-circle" theme="success" />
            </div>
            <div className="col-md-4 col-lg">
              <StatWidget label="Total Withdrawals (RC)" value={formatNumber(a?.totalWithdrawalsRc ?? 0, 2)} icon="fe fe-arrow-up-circle" theme="danger" />
            </div>
            <div className="col-md-4 col-lg">
              <StatWidget label="Total Credit (RC)" value={formatNumber(a?.totalCreditRc ?? 0, 2)} icon="fe fe-credit-card" theme="info" />
            </div>
            <div className="col-md-4 col-lg">
              <StatWidget label="Hot Country" value={a?.hotCountry || 'N/A'} icon="fe fe-map-pin" theme="warning" />
            </div>
            <div className="col-md-4 col-lg">
              <StatWidget label="Number Of Clients" value={(a?.numberOfLogins ?? 0).toLocaleString()} icon="fe fe-users" theme="primary" />
            </div>
          </div>

          <DataGrid
            columns={columns}
            data={table.rows}
            rowKey={(r) => String(r.id)}
            server={{
              pageIndex: table.pageIndex,
              pageSize: table.pageSize,
              pageCount: table.pageCount,
              totalCount: table.totalCount,
              onPageChange: table.setPageIndex,
              onPageSizeChange: table.setPageSize,
            }}
            columnSearch
            columnSearchValues={table.columnFilters}
            onColumnSearchChange={(key, value) => table.setColumnFilter(key, value)}
            rowActions={
              canEdit
                ? (row) =>
                    editing?.id === row.id ? (
                      <div className="d-flex gap-1">
                        <button type="button" className="btn btn-sm btn-outline-primary" title="Save" onClick={() => saveInline(row)}>
                          <i className="fa fa-check" aria-hidden="true" />
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" onClick={() => setEditing(null)}>
                          <i className="fa fa-times" aria-hidden="true" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        title="Edit Provider"
                        onClick={() => setEditing({ id: row.id, provider: row.provider ?? '' })}
                      >
                        <i className="fa fa-pencil" aria-hidden="true" />
                      </button>
                    )
                : undefined
            }
            emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
          />
        </div>
      </div>

      {editState && (
        <EditProviderDialog
          show
          oldProvider={editState.row.provider ?? ''}
          newProvider={editState.newProvider}
          onSubmit={submitEdit}
          onClose={() => setEditState(null)}
        />
      )}
    </div>
  );
}
